import { useMutation } from '@tanstack/react-query';
import * as z from 'zod';

import { CURRENCIES } from 'src/features/payStations/types';
import { RESERVATION_TYPE, type ReservationType } from 'src/features/reservations/types/reservationType';
import axios from 'src/lib/axios';
import { type MutationConfig } from 'src/lib/react-query';
import { type GeneralSmartParkError } from 'src/types';
import { formatResourcePath } from 'src/utils/regex';

const RESOURCE = '/reservations/:parkingId/vehicle-stay-reservation-type/';

export const createReservationTypeFormSchema = z.object({
    reservation_type: z.nativeEnum(RESERVATION_TYPE).default(RESERVATION_TYPE.HOURLY),
    allowed_spaces: z.number(),
    price: z.coerce.number().gt(0).optional(),
    tax_percent: z.string().regex(/^-?\d{0,20}(?:\.\d{0,4})?$/),
    currency: z.nativeEnum(CURRENCIES).default(CURRENCIES.EUR),
    tax_amount: z.coerce.number().gt(0).optional(),
    price_with_tax: z.coerce.number().gt(0),
    reserve_space: z.boolean().default(false),
    entry_tolerance_minutes: z.number().optional(),
    exit_tolerance_minutes: z.number().optional(),
    valid_from: z.coerce.date(),
    name: z.string().min(1).max(255),
    description: z.string().optional().default(''),
    points: z.array(z.string()).optional(),
});
export type CreateReservationTypeFormSchemaValues = z.infer<typeof createReservationTypeFormSchema>;

export const createFixedReservationTypeFormSchema = z.object({
    reservation_type: z.nativeEnum(RESERVATION_TYPE).default(RESERVATION_TYPE.HOURLY),
    allowed_spaces: z.number(),
    price: z.coerce.number().gt(0).optional(),
    tax_percent: z.string().regex(/^-?\d{0,20}(?:\.\d{0,4})?$/),
    currency: z.nativeEnum(CURRENCIES).default(CURRENCIES.EUR),
    tax_amount: z.coerce.number().gt(0).optional(),
    price_with_tax: z.coerce.number().gt(0),
    reserve_space: z.boolean().default(false),
    entry_tolerance_minutes: z.number().optional(),
    exit_tolerance_minutes: z.number().optional(),
    valid_from: z.coerce.date(),
    valid_to: z.coerce.date(),
    name: z.string().min(1).max(255),
    description: z.string().optional().default(''),
    fixed_duration_start: z.coerce.date(),
    fixed_duration_end: z.coerce.date(),
    points: z.array(z.string()).optional(),
});
export type CreateFixedReservationTypeFormSchemaValues = z.infer<typeof createFixedReservationTypeFormSchema>;

interface DTO extends CreateReservationTypeFormSchemaValues {
    parkingId: string;
}

type MutationFnType = typeof mutationFn;
const mutationFn = ({ parkingId, ...data }: DTO): Promise<ReservationType> => {
    return axios.post(formatResourcePath(RESOURCE, { parkingId }), data);
};

interface UseMutationOptions extends MutationConfig<MutationFnType, GeneralSmartParkError> {}
const useMutationHook = (options: UseMutationOptions = {}) =>
    useMutation({
        mutationFn,
        ...options,
    });

export { useMutationHook as useCreateReservationType };
